import { Link } from '@tanstack/react-router';

import type { AssignedMany, RosterPage } from '../api/roster.ts';
import { Button } from '../components/button.tsx';
import { Popover } from '../components/popover.tsx';
import { misfitReason } from '../roster-rows.ts';
import { ShiftForm } from './crew-panel.tsx';
import { OfferPopover } from './offer-popover.tsx';
import { stateWord } from './roster-bars.tsx';
import { ShiftPopover } from './shift-popover.tsx';
import type { Picked } from './roster.tsx';
import { TaskForm, TaskRow } from './tasks-panel.tsx';
import { TimeOffForm, TimeOffPopover } from './time-off.tsx';

// CB-CONV §12 crew part 4B: what the roster's popover shows, as the operator's boards of 2026-10-26
// draw it — one item, a new one, or a question about several (docs/designs/roster-page.md §2).

export type Opened =
  | { kind: 'shift' | 'task' | 'offer'; id: string }
  | {
      kind: 'add';
      what: 'shift' | 'task' | 'offer' | 'unavailable' | 'leave';
      drawn?: Drawn | undefined;
    }
  /** CRW-02 at v1.9106: a block behind a row, by its record and where this occurrence starts. */
  | { kind: 'timeOff'; id: string; startsAt: string }
  | {
      kind: 'mismatch';
      shiftId: string;
      /** Every task ticked, those already put on included. */
      taskIds: string[];
      items: AssignedMany['mismatched'];
    }
  | { kind: 'cancel' };

/** OPS-03 at v1.9105: a window drawn on the roster, filling in the new shift, offer or task. */
export interface Drawn {
  /** Wall-clock in the roster's zone. */
  readonly start: string;
  readonly end: string;
  readonly crewMemberId?: string | undefined;
  readonly jobId?: string | undefined;
}

/** What a bulk action did, and each it refused, by name. */
export interface Outcome {
  readonly done: string;
  readonly refused: ReadonlyArray<{ name: string; reason: string }>;
}

const hhmm = (local: string) => local.slice(11, 16);

export function RosterPopover({
  opened,
  date,
  data,
  mayManage,
  mayCrew,
  mayApprove,
  zone,
  seesPrices,
  picked,
  busy,
  onAssignAnyway,
  onCancelShifts,
  onChanged,
  onClose,
}: {
  opened: Opened;
  date: string;
  data: RosterPage | undefined;
  mayManage: boolean;
  /** crew.manage: enters and removes unavailability and leave. */
  mayCrew: boolean;
  /** leave.approve. */
  mayApprove: boolean;
  zone: string;
  seesPrices: boolean;
  picked: Picked | null;
  busy: boolean;
  onAssignAnyway: (shiftId: string, taskIds: string[]) => void;
  onCancelShifts: (shiftIds: string[]) => void;
  onChanged: () => Promise<void>;
  onClose: () => void;
}) {
  if (opened.kind === 'add' && (opened.what === 'unavailable' || opened.what === 'leave')) {
    const crewMemberId = opened.drawn?.crewMemberId;
    if (!crewMemberId || !opened.drawn) return null;
    const name = data?.people.find((p) => p.id === crewMemberId)?.name ?? '';
    return (
      <Popover
        title={opened.what === 'unavailable' ? 'Unavailable' : 'Leave'}
        subtitle={name}
        onClose={onClose}
      >
        <TimeOffForm
          kind={opened.what}
          crewMemberId={crewMemberId}
          drawn={opened.drawn}
          onDone={onClose}
        />
      </Popover>
    );
  }
  if (opened.kind === 'timeOff') {
    const entry = data?.timeOff.find((t) => t.id === opened.id && t.startsAt === opened.startsAt);
    if (!entry) return null;
    return (
      <TimeOffPopover
        entry={entry}
        name={data?.people.find((p) => p.id === entry.crewMemberId)?.name ?? ''}
        zone={zone}
        mayManage={mayCrew}
        mayApprove={mayApprove}
        onClose={onClose}
      />
    );
  }
  if (opened.kind === 'add') {
    const title = {
      shift: 'New shift',
      task: 'New task',
      offer: 'New offer',
      unavailable: 'Unavailable',
      leave: 'Leave',
    }[opened.what];
    return (
      <Popover title={title} onClose={onClose}>
        {opened.what === 'task' ? (
          <TaskForm date={date} drawn={opened.drawn} onDone={onClose} />
        ) : (
          <ShiftForm
            date={date}
            drawn={opened.drawn}
            seesPrices={seesPrices}
            asOfferFirst={opened.what === 'offer'}
            onDone={onClose}
          />
        )}
      </Popover>
    );
  }
  if (opened.kind === 'mismatch') {
    return (
      <Mismatch
        opened={opened}
        data={data}
        busy={busy}
        onAssignAnyway={onAssignAnyway}
        onClose={onClose}
      />
    );
  }
  if (opened.kind === 'cancel') {
    const ids = picked?.kind === 'shift' ? picked.ids : [];
    return (
      <Popover
        title={`Cancel ${ids.length} ${ids.length === 1 ? 'shift' : 'shifts'}?`}
        onClose={onClose}
        footer={
          <>
            <Button disabled={busy || ids.length === 0} onClick={() => onCancelShifts(ids)}>
              Cancel shifts
            </Button>
            <Button variant="quiet" onClick={onClose}>
              Back
            </Button>
          </>
        }
      >
        <p className="text-sm">
          Each is cancelled on its own; one that can’t be is named, and the rest stand. Tell the
          people on them yourself.
        </p>
      </Popover>
    );
  }
  if (opened.kind === 'shift') {
    const shift = data?.shifts.find((s) => s.id === opened.id);
    if (!shift) return null;
    return (
      <ShiftPopover
        shift={shift}
        tasks={data?.tasks ?? []}
        mayManage={mayManage}
        seesPrices={seesPrices}
        onChanged={onChanged}
        onClose={onClose}
      />
    );
  }
  if (opened.kind === 'task') {
    const task = data?.tasks.find((t) => t.id === opened.id);
    if (!task) return null;
    return (
      <Popover
        title={task.description}
        subtitle={`${hhmm(task.window.localStart)} – ${hhmm(task.window.localEnd)}${task.venue ? ` · ${task.venue.name}` : ''}`}
        onClose={onClose}
      >
        {task.job ? (
          <JobLink id={task.job.id} number={task.job.number} name={task.job.name} />
        ) : null}
        <ul aria-label="The task you opened">
          <TaskRow task={task} editable={mayManage && !task.job?.cancelled} />
        </ul>
      </Popover>
    );
  }
  const offer = data?.offers.find((o) => o.id === opened.id);
  if (!offer) return null;
  return (
    <OfferPopover
      offer={offer}
      mayManage={mayManage}
      seesPrices={seesPrices}
      onChanged={onChanged}
      onClose={onClose}
    />
  );
}

/**
 * OPS-10: the ticked tasks that don't fit the shift, asked about together. Those that fit are on
 * already; *Put on only those that fit* leaves the rest off, *Put all on anyway* sends the rest
 * again, confirmed.
 */
function Mismatch({
  opened,
  data,
  busy,
  onAssignAnyway,
  onClose,
}: {
  opened: Extract<Opened, { kind: 'mismatch' }>;
  data: RosterPage | undefined;
  busy: boolean;
  onAssignAnyway: (shiftId: string, taskIds: string[]) => void;
  onClose: () => void;
}) {
  const shift = data?.shifts.find((s) => s.id === opened.shiftId);
  const misfit = new Map(opened.items.map((m) => [m.taskId, m]));
  const rows = opened.taskIds.map((id) => {
    const task = data?.tasks.find((t) => t.id === id);
    const m = misfit.get(id);
    // The database judged it; the times say how, else its own words do (a venue, say).
    const how = m && task && shift ? misfitReason(task.window, shift.window, m.venueDiffers) : null;
    return {
      id,
      name: task?.description ?? 'A task',
      when: task ? `${hhmm(task.window.localStart)} – ${hhmm(task.window.localEnd)}` : '',
      why: m ? (how && how !== 'Fits' ? how : m.reason) : null,
    };
  });
  const fits = rows.length - opened.items.length;
  const person = shift?.crewMember?.name;
  return (
    <Popover
      title={`${opened.items.length} of ${rows.length} tasks don’t fit ${person ? `${person}’s` : 'the'} shift`}
      subtitle={
        shift
          ? `${person ?? 'Unfilled'} · ${stateWord(shift).toLowerCase()} · ${hhmm(shift.window.localStart)} – ${hhmm(shift.window.localEnd)}`
          : undefined
      }
      onClose={onClose}
      footer={
        <>
          <span className="flex-1" />
          <Button variant="quiet" onClick={onClose}>
            {fits > 0
              ? `Put on only the ${fits} that ${fits === 1 ? 'fits' : 'fit'}`
              : 'Leave them off'}
          </Button>
          <Button
            disabled={busy}
            onClick={() =>
              onAssignAnyway(
                opened.shiftId,
                opened.items.map((m) => m.taskId),
              )
            }
          >
            Put all {rows.length} on anyway
          </Button>
        </>
      }
    >
      <table className="data-table text-sm">
        <thead>
          <tr>
            <th scope="col">Task</th>
            <th scope="col">Task time</th>
            <th scope="col">Why</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{r.name}</td>
              <td>{r.when}</td>
              <td>{r.why ?? <span className="tag tag-accepted">Fits</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Popover>
  );
}

function JobLink({ id, number, name }: { id: string; number: number; name: string }) {
  return (
    <Link to="/jobs/$jobId" params={{ jobId: id }} className="link self-start text-sm">
      J-{number} — {name}
    </Link>
  );
}
